import { FeatureAccessFeature } from 'constants/FeatureAccessFeature';
import {
  FEATURE_ACCESS__COMPANY_NOT_FOUND,
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  FEATURE_ACCESS__NO_ACCESS,
  FEATURE_ACCESS__TIME_REGISTRATION_RUNNING,
} from 'translations/constants';
import {
  IBulkFeatureAccessCompanyResult,
  IBulkFeatureAccessResponse,
  ICompanyFeatureAccess,
  IFeatureState,
  ParsedFeatureAccessError,
} from 'types/feature-access/IFeatureAccess';
import { FEATURES } from './constants';

export type FeatureStateMap = Record<FeatureAccessFeature, boolean>;

/** Flat so each feature is an accessorKey the table can sort on. */
export type IFeatureAccessRow = FeatureStateMap & { companyId: number; companyName: string };

export type FeatureFilter = FeatureAccessFeature | '';
export type StatusFilter = 'enabled' | 'disabled' | '';

export const toFeatureStateMap = (features: IFeatureState[]): FeatureStateMap =>
  FEATURES.reduce(
    (map, feature) => ({ ...map, [feature]: features.find(state => state.feature === feature)?.enabled ?? false }),
    {} as FeatureStateMap,
  );

export const toFeatureStates = (map: FeatureStateMap): IFeatureState[] =>
  FEATURES.map(feature => ({ feature, enabled: map[feature] }));

export const toFeatureAccessRow = (company: ICompanyFeatureAccess): IFeatureAccessRow => ({
  companyId: company.companyId,
  companyName: company.companyName,
  ...toFeatureStateMap(company.features),
});

/** Lower-cases and strips diacritics so "cafe" finds "Café". */
const normalise = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Search matches part of the company name. The feature/status filter only applies once both
 * dropdowns are set (AC "the filter is applied when both fields are populated").
 */
export const filterFeatureAccessRows = (
  rows: IFeatureAccessRow[],
  searchTerm: string,
  feature: FeatureFilter,
  status: StatusFilter,
): IFeatureAccessRow[] => {
  const term = normalise(searchTerm);
  const filterActive = feature !== '' && status !== '';
  if (!term && !filterActive) return rows;

  return rows.filter(row => {
    if (term && !normalise(row.companyName).includes(term)) return false;
    if (filterActive && row[feature as FeatureAccessFeature] !== (status === 'enabled')) return false;
    return true;
  });
};

/**
 * Keeps only selected ids that are still in `rows`, so a narrowed filter or a reload can never leave
 * an invisible Company in the bulk selection. Returns the same array when nothing is dropped, so a
 * caller's state setter can bail out without re-rendering.
 */
export const pruneSelection = (selected: number[], rows: IFeatureAccessRow[]): number[] => {
  const ids = new Set(rows.map(row => row.companyId));
  const kept = selected.filter(id => ids.has(id));
  return kept.length === selected.length ? selected : kept;
};

export const isAllSelected = (selected: number[], rows: IFeatureAccessRow[]): boolean => {
  if (rows.length === 0) return false;
  const chosen = new Set(selected);
  return rows.every(row => chosen.has(row.companyId));
};

/** "Select all" covers the whole filtered result across pages, not just the visible page. */
export const toggleSelectAll = (selected: number[], rows: IFeatureAccessRow[]): number[] =>
  isAllSelected(selected, rows) ? [] : rows.map(row => row.companyId);

export interface SaveOutcome {
  granted: boolean;
  removed: boolean;
}

/** Derived from what the user changed in the panel, not from the server response. */
export const getSaveOutcome = (before: FeatureStateMap, after: FeatureStateMap): SaveOutcome => ({
  granted: FEATURES.some(feature => !before[feature] && after[feature]),
  removed: FEATURES.some(feature => before[feature] && !after[feature]),
});

export type BulkOption = 'enable' | 'disable' | 'unchanged';
export type BulkOptions = Record<FeatureAccessFeature, BulkOption>;

export const DEFAULT_BULK_OPTIONS: BulkOptions = FEATURES.reduce(
  (options, feature) => ({ ...options, [feature]: 'unchanged' }),
  {} as BulkOptions,
);

/** Enable → true, Disable → false, Leave unchanged → omitted. */
export const buildBulkChanges = (options: BulkOptions): IFeatureState[] =>
  FEATURES.filter(feature => options[feature] !== 'unchanged').map(feature => ({
    feature,
    enabled: options[feature] === 'enable',
  }));

export const getBlockedCompanies = (response: IBulkFeatureAccessResponse): IBulkFeatureAccessCompanyResult[] =>
  response.companies.filter(company => company.blocked.length > 0);

const HANDLED_KEYS = [
  FEATURE_ACCESS__COMPANY_NOT_FOUND,
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  FEATURE_ACCESS__TIME_REGISTRATION_RUNNING,
];

/**
 * Picks the translation key to show for a failed call. UNKNOWN_FEATURE / DUPLICATE_FEATURE /
 * NO_COMPANIES_SELECTED / ModelState 400s are client bugs and get the generic `fallbackKey`.
 */
export const getFeatureAccessErrorKey = (parsed: ParsedFeatureAccessError, fallbackKey: string): string => {
  if (parsed.key && HANDLED_KEYS.includes(parsed.key)) return parsed.key;
  if (parsed.status === 403) return FEATURE_ACCESS__NO_ACCESS;
  return fallbackKey;
};
